/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import type { Day, Searchsummary } from 'lumen/models';
import { getWeekendPlotBands } from 'lumen/utils/charts/chart-utils';
import { MI_SEARCHES_TYPE_FLIGHTS_LEISURE } from 'lumen/utils/constants/general-constants';
import { FIXED_EXTRACT_DATE_LATEST } from 'lumen/utils/constants/market-insight-constants';
import { xAxisLabelFormatterForOverview } from 'lumen/utils/market-insight/market-insight-helpers';

interface HotelMarketInsightChartsChartOverallSearchesSignature {
  Args: {
    currentExtractDate: unknown;
    dayModel: unknown;
    onUpdateChartReference: unknown;
    searchesType: unknown;
  };
}

export default class HotelMarketInsightChartsChartOverallSearches extends Component<HotelMarketInsightChartsChartOverallSearchesSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare miSearches: Services['mi-searches'];
  @service declare miSelections: Services['mi-selections'];
  @service declare store: Services['store'];

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.dayModel.id',
    'args.{currentExtractDate,searchesType}',
    'daysToDisplay.range.length',
    'daysToDisplay.{endRangeCorrection,startRangeCorrection}',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const chartData = [
      {
        name: 'flights-overall-searches',
        data: [],
      },
    ];

    // Array to store all searchsummary objects for the current range
    // @ts-expect-error: Incorrect type
    const searchSummaryObjectsForDateRangeAndExtractDate = [];
    // Object where we can store all country search objects per day
    const countrySearchObjectsPerDay = {};

    // Get all searchsummary objects for the days in this range
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.daysToDisplay.range.length && this.args.currentExtractDate) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.daysToDisplay.range.forEach((rangeDay) => {
        // Form the correct search summary id for each day in the range (allows us to peek in the store)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const composedId = this.miSearches.composeSearchSummaryId(
          this.args.searchesType,
          rangeDay,
          this.args.currentExtractDate,
        );

        // Peek the search summary in the store and add it to an array to store with all objects from the current range
        const searchSummaryObject = this.store.peekRecord<Searchsummary>(
          'searchsummary',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          composedId,
        );
        if (searchSummaryObject) {
          searchSummaryObjectsForDateRangeAndExtractDate.push(
            searchSummaryObject,
          );
        }

        // In this chart we need to retrieve the country search objects as well: we need to show search breakdown / country in the tooltip
        if (
          this.args.searchesType === MI_SEARCHES_TYPE_FLIGHTS_LEISURE &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.store.peekRecord<Day>('day', rangeDay)
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const dayObject = this.store.peekRecord<Day>('day', rangeDay);

          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const countrySearchObjects =
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.miSearches.filterCountrySearchesByExtractDate(
              MI_SEARCHES_TYPE_FLIGHTS_LEISURE,
              dayObject,
              this.args.currentExtractDate,
            );

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          countrySearchObjectsPerDay[rangeDay] = countrySearchObjects;
        }
      });
    }

    if (searchSummaryObjectsForDateRangeAndExtractDate.length) {
      if (this.daysToDisplay.startRangeCorrection) {
        for (let i = 0; i < this.daysToDisplay.startRangeCorrection; i++) {
          // @ts-expect-error: Incorrect type
          chartData[0].data.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            x: this.xValue(this.daysToDisplay.range[i]),
            y: 0,
            isFiller: true,
            isBeforeMinDate: true,
          });
        }
      }

      // @ts-expect-error: Incorrect type
      searchSummaryObjectsForDateRangeAndExtractDate.forEach(
        (searchSummary) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const searchSummaryDayId = searchSummary.day.id;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const isoDayOfWeek = searchSummary.day.date.isoWeekday();

          let columnColor = '';

          // @ts-expect-error: Incorrect type
          if (searchSummaryDayId === this.args.dayModel.id) {
            columnColor =
              'var(--prism-color-chart-accent-secondary-figure-emphasis)';
          } else {
            columnColor =
              'var(--prism-color-chart-accent-secondary-figure-subdued)';
          }

          const dowTranslation = this.intl.t(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            `days.${REVERSE_ISO_DAYS[isoDayOfWeek]}`,
          );
          const tooltipMessage = this.intl.t(
            'market_insight.search_volume_level',
            {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              level: searchSummary.numSearchesAsTooltipLabel,
              day: dowTranslation,
            },
          );

          // @ts-expect-error: Incorrect type
          chartData[0].data.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            x: this.xValue(searchSummary.day.date),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            y: searchSummary.numSearches,
            tooltipMessage,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            day: searchSummaryDayId,
            color: columnColor,
          });
        },
      );

      if (this.daysToDisplay.endRangeCorrection) {
        for (let i = 0; i < this.daysToDisplay.endRangeCorrection; i++) {
          // @ts-expect-error: Incorrect type
          chartData[0].data.push({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            x: this.daysToDisplay.range[
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.daysToDisplay.range.length -
                (this.daysToDisplay.endRangeCorrection - i)
            ],
            y: 0,
            isFiller: true,
            isAfterMaxDate: true,
          });
        }
      }
    }

    return chartData;
  }

  @computed(
    'point.{day,isAfterMaxDate,isBeforeMinDate,isFiller,tooltipMessage}',
    'value',
    'weekendPlotBands',
    'yAxisMaxValue',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;

    return {
      chart: {
        type: 'column',
        height: 128,
        zoomType: false,
        animation: false,
      },
      title: {
        text: context.intl.t('market_insight.searches.search_level'),
        x: 3,
        margin: 0,
        style: {
          display: 'visible',
          fontSize: 12,
          textAlign: 'center',
          color: '#4c657b',
        },
      },
      xAxis: {
        type: 'datetime',
        lineWidth: 1,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        tickPositioner() {
          return [
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            context.xValue(context.daysToDisplay.range[0]),
            // @ts-expect-error: Incorrect type
            context.xValue(context.args.dayModel.date),
            context.xValue(
              context.daysToDisplay.range[
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                context.daysToDisplay.range.length - 1
              ],
            ),
          ];
        },
        labels: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          formatter() {
            return xAxisLabelFormatterForOverview(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.value,
              context.miSelections.isDailyOverview,
              context.intl,
            );
          },
        },
        plotBands: this.weekendPlotBands,
      },
      yAxis: {
        labels: {
          enabled: false,
        },
        max: this.yAxisMaxValue,
        // Following option was to make sure that Highcharts followed the max value
        // https://api.highcharts.com/highcharts/yAxis.startOnTick
        // https://api.highcharts.com/highcharts/yAxis.endOnTick
        startOnTick: false,
        endOnTick: false,
        title: {
          text: '',
        },
        gridLineWidth: 0,
      },
      legend: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'var(--prism-color-background-inverse)',
        borderWidth: 1,
        borderColor: 'var(--prism-color-background-inverse)',
        padding: 12,
        style: {
          color: 'var(--prism-color-text-inverse)',
          fontSize: '12px',
        },
        borderRadius: 3,
        shadow: false,
        shape: 'callout',
        outside: true,
        useHTML: true,
        positioner: null,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // First check the point is not a filler point we've added to keep the selected day in the middle (=> day earlier than min date or after max date)
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (!this.point.isFiller) {
            return `
              <span class="text-200--bold">${
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                dayjs(this.point.day).format(
                  context.miSelections.defaultDateFormatWithDOW,
                )
              }</span>
              <br />
              <div style="margin-top:4px;">${
                // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                this.point.tooltipMessage
              }</div>
            `;
          }

          // Separate message for days that were added to fill up the chart
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (this.point.isBeforeMinDate) {
            return context.intl
              .t('market_insight.flight_patterns.message_before_min_date')
              .toString();
          }

          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (this.point.isAfterMaxDate) {
            return context.intl
              .t('market_insight.flight_patterns.message_after_max_date')
              .toString();
          }

          return context.intl.t('general.not_available_short').toString();
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0,
          borderWidth: 0,
          groupPadding: 0,
          pointWidth: 4,
        },
        series: {
          animation: false,
          minPointLength: 3, // Have a min height for the bars (otherwise bars with very few searches in the future will not be visible at all & the chart looks broken)
        },
      },
    };
  }

  @computed('args.{dayModel.date}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get daysToDisplay() {
    const { maxAllowedDay, minAllowedDay } = this.miSelections;

    // In this chart we need to show values for the days in the following range:
    // today -14 days -> today -> today +14 days (so a total of 29 days)
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const currentSelectedDate = this.args.dayModel.date;

    // Set the basic scenario date range limits
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const dateRangeStart = currentSelectedDate.subtract(14, 'day');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const dateRangeEnd = currentSelectedDate.add(14, 'day');

    // We have some edge cases when selecting a day close to today (we don't have data of days in the past, so the range can cause issues)
    // Similar case for days close to the max allowed day
    // We will always try to get to 29 datapoints, but if the days are outside of the allowed range we'll fill them with dummy objects.
    // These variables store how many dummy days we should add at the beginning or end.
    let startRangeCorrection = 0;
    let endRangeCorrection = 0;

    // Perform some extra checks to make sure we respect the min/max allowed date
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    if (minAllowedDay && dateRangeStart.isBefore(minAllowedDay, 'day')) {
      // The daterange start can't be earlier than the min allowed date. Do corrections in this case
      // Number of days we can show between the min allowed date and the current selected day
      const startRangeDifference = Math.abs(
        this.hotelAndMarketService
          // @ts-expect-error: Incorrect type
          .toLocalTime(minAllowedDay)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          .diff(currentSelectedDate, 'day'),
      );

      startRangeCorrection = 14 - startRangeDifference;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    } else if (maxAllowedDay && dateRangeEnd.isAfter(maxAllowedDay, 'day')) {
      // Number of days we can show between the max allowed date and the current selected day
      const endRangeDifference = Math.abs(
        this.hotelAndMarketService
          // @ts-expect-error: Incorrect type
          .toLocalTime(maxAllowedDay)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          .diff(currentSelectedDate, 'day'),
      );

      endRangeCorrection = 14 - endRangeDifference;
    }

    // Get an array containing all days we need to visualise in this chart
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const dateRangeToShow = this.miSearches.generateDaysBetweenTwoMomentObjects(
      dateRangeStart,
      dateRangeEnd,
    );

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      range: dateRangeToShow,
      startRangeCorrection,
      endRangeCorrection,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekendPlotBands() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const currentSelectedDate = this.args.dayModel.date;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const startDate = currentSelectedDate.subtract(14, 'day');
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const endDate = currentSelectedDate.add(14, 'day');

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return getWeekendPlotBands(this.hotelAndMarketService, startDate, endDate);
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxisMaxValue() {
    // We try to determine an Y axis max ourselves. If we don't do this, Highcharts will try it themselves and it can result in inconsistent Y axis scales when using the evolution slider.
    // Main issue is that values that are actually lower can look way bigger because of this.

    // As the Y axis max we take the max value in the date range we show, always using the latest extract date.
    // The values for the latest extract date should be the actual max, and earlier values in the evolution should be smaller (dixit Joanna)
    if (this.daysToDisplay.range) {
      // @ts-expect-error: Incorrect type
      const searchSummaryObjectsForLatestExtractDate = [];

      // @ts-expect-error: Incorrect type
      let searchSummaryNumberOfSearchesUpperBound = null;

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.daysToDisplay.range.forEach((rangeDay) => {
        // Form the correct search summary id for each day in the range (allows us to peek in the store)
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        const composedId = this.miSearches.composeSearchSummaryId(
          this.args.searchesType,
          rangeDay,
          FIXED_EXTRACT_DATE_LATEST,
        );

        // Peek the search summary in the store and add it to an array to store with all objects from the current range
        const searchSummaryObject = this.store.peekRecord<Searchsummary>(
          'searchsummary',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          composedId,
        );
        if (searchSummaryObject) {
          searchSummaryObjectsForLatestExtractDate.push(
            searchSummaryObject.numSearches,
          );

          // Get the numSearchesUpperBound from 1 searchsummary. Should be the same value in every searchsummary.
          // @ts-expect-error: Incorrect type
          if (!searchSummaryNumberOfSearchesUpperBound) {
            searchSummaryNumberOfSearchesUpperBound =
              searchSummaryObject.numSearchesUpperBound;
          }
        }
      });

      // Get the max numSearches present in all searchsummaries in the range (for the latest extract date)
      const maxNumberOfSearchesInShownDateRange = Math.max(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ...searchSummaryObjectsForLatestExtractDate,
      );

      // Take the max of the upperbound API value & the max we just calculated (needed because the max can be higher than the upperbound value by API in some cases as it's an percentile value)
      return Math.max(
        // @ts-expect-error: Incorrect type
        searchSummaryNumberOfSearchesUpperBound,
        maxNumberOfSearchesInShownDateRange,
      );
    }

    return null;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  xValue(date) {
    return this.hotelAndMarketService.toLocalUnixTime(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.hotelAndMarketService.toLocalTime(date).unix(),
    );
  }

  <template>
    <GeneralChartsOtaChart
      {{! @glint-expect-error: Incorrect type }}
      @chartOptions={{this.chartOptions}}
      @model={{this.chartData}}
      {{! @glint-expect-error: Incorrect type }}
      @onUpdateChartReference={{@onUpdateChartReference}}
    />
  </template>
}
